import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import qs from 'qs';

import { Paper, Grid, Typography, Tooltip, IconButton } from '@material-ui/core';
import { Pagination } from '@material-ui/lab';

import Filter from 'components/Filter/components';
import FilterMobile from 'components/FilterMobile/components';
import MTable from 'components/MTable';
import TableMobile from 'components/TableMobile';
import Button from 'components/Button';
import ProductsCategoriesRegistration from 'components/ProductsCategoriesRegistration';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCirclePlus, faTrash, faPen } from '@fortawesome/free-solid-svg-icons';

import { loadProductCategories, removeProductCategory } from '../store/thunk';
import { setSingleProductCategory, clearSingleProductCategory } from '../store/actions';

import useWindowDimensions from 'helpers/hooks/useWindowDimensions';
import { capitalizeString } from 'helpers/formatters';

const headers = [
  { title: 'Grupo de Categoria', field: 'group' },
  { title: 'Descrição', field: 'description' }
];

export default function ProductsCategories() {
  const dispatch = useDispatch();

  const { width } = useWindowDimensions();
  const isMobile = width < 1024;

  const { userId } = useSelector(state => state.profile);
  const { establishmentId } = useSelector(state => state.businessInfo);
  const { productTypes } = useSelector(state => state.products.filters);
  const { list, total, isLoading } = useSelector(state => state.productsCategories);

  const [isCategoryRegistrationModalOpen, setIsCategoryRegistrationModalOpen] = useState(false);
  const [search, setSearch] = useState('');

  const productsCategoriesPageSize = 10;
  const [productsCategoriesPage, setProductsCategoriesPage] = useState(1);

  const productTypesData = list
    ?.map(type => {
      return {
        ...type,
        description: capitalizeString(type?.description) || '-',
        group: capitalizeString(productTypes?.find(product => +product?.id == +type?.parentProductTypeId)?.description) || '-'
      }
    })
    ?.filter(type => {
      if(search) {
        return type?.description?.includes(capitalizeString(search)) || type?.group?.includes(capitalizeString(search));
      }

      return type;
    });

  const productTypesDataPage = productTypesData
    ?.slice(productsCategoriesPageSize * productsCategoriesPage - productsCategoriesPageSize, productsCategoriesPageSize * productsCategoriesPage);

  const productsCategoriesPageCount = !!search ? Math.ceil(productTypesData?.length / productsCategoriesPageSize) : Math.ceil(total / productsCategoriesPageSize);

  useEffect(() => {
    setProductsCategoriesPage(1);
    dispatch(loadProductCategories(userId, establishmentId));
  }, [establishmentId]);

  return(
    <Paper>
      {isCategoryRegistrationModalOpen && (
        <ProductsCategoriesRegistration
          open={isCategoryRegistrationModalOpen}
          onClose={() => setIsCategoryRegistrationModalOpen(false)}
        />
      )}
      {isMobile ? (
        <FilterMobile
          filter
          hasSearchTerm
          handlePageRequest={({ qp }) => {
            const { search } = qs.parse(qp);
            setSearch(search);
          }}
          actionButton={
            <Button
              type="button"
              color="success"
              onClick={() => {
                dispatch(clearSingleProductCategory());
                setIsCategoryRegistrationModalOpen(true);
              }}
              startIcon={
                <FontAwesomeIcon
                  icon={faCirclePlus}
                  color="#FFFFFF"
                  size="xs"
                />
              }
            >
              Nova Categoria
            </Button>
          }
        />
      ) : (
        <Filter
          filter
          hasSearchTerm
          handlePageRequest={({ qp }) => {
            const { search } = qs.parse(qp);
            setSearch(search);
          }}
          actionButton={
            <Button
              type="button"
              color="success"
              onClick={() => {
                dispatch(clearSingleProductCategory());
                setIsCategoryRegistrationModalOpen(true);
              }}
              startIcon={
                <FontAwesomeIcon
                  icon={faCirclePlus}
                  color="#FFFFFF"
                  size="xs"
                />
              }
            >
              Nova Categoria
            </Button>
          }
        />
      )}
      {isMobile && (
        <TableMobile
          loading={isLoading}
          data={productTypesDataPage?.map(item => {
            return {
              ...item,
              groupItem: {
                index: 1,
                label: 'Grupo de Categoria',
                value: <Typography color='textSecondary' variant='subtitle2'>{item?.group}</Typography>
              },
              descriptionItem: {
                index: 2,
                label: 'Descrição',
                value: <Typography color='textSecondary' variant='subtitle2'>{item?.description}</Typography>
              }
            }
          })}
          actions={[
            {
              action: rowData => (
                <Tooltip
                  placement='top'
                  title={!rowData?.establishmentUuid ? 'Categoria padrão não pode ser excluída' : 'Excluir categoria'}
                  enterTouchDelay={0}
                >
                  <IconButton onClick={() => {
                    if(!rowData?.establishmentUuid) {
                      return;
                    }

                    dispatch(removeProductCategory(userId, establishmentId, rowData?.id));
                  }}>
                    <FontAwesomeIcon
                      icon={faTrash}
                      color={!rowData?.establishmentUuid ? "rgba(246, 108, 107, 0.5)" : "#F66C6B"}
                      size="xs"
                    />
                  </IconButton>
                </Tooltip>
              )
            },
            {
              action: rowData => (
                <Tooltip
                  placement='top'
                  title={!rowData?.establishmentUuid ? 'Categoria padrão não pode ser editada' : 'Editar categoria'}
                  enterTouchDelay={0}
                >
                  <IconButton onClick={() => {
                    if(!rowData?.establishmentUuid) {
                      return;
                    }

                    const { id, parentProductTypeId, description } = rowData;

                    dispatch(setSingleProductCategory({ id, parentProductTypeId, description }));
                    setIsCategoryRegistrationModalOpen(true);
                  }}>
                    <FontAwesomeIcon
                      icon={faPen}
                      color={!rowData?.establishmentUuid ? "rgba(2, 42, 92, 0.5)" : "#022A5C"}
                      size="xs"
                    />
                  </IconButton>
                </Tooltip>
              )
            }
          ]}
        />
      )}
      {!isMobile && (
        <MTable
          loading={isLoading}
          headers={headers}
          data={productTypesDataPage}
          actions={[
            rowData => ({
              icon: () => (
                <FontAwesomeIcon
                  icon={faTrash}
                  color={!rowData?.establishmentUuid ? "rgba(246, 108, 107, 0.5)" : "#F66C6B"}
                  size="xs"
                />
              ),
              disabled: !rowData?.establishmentUuid,
              tooltip: !rowData?.establishmentUuid ? 'Categoria padrão não pode ser excluída' : 'Excluir categoria',
              onClick: () => dispatch(removeProductCategory(userId, establishmentId, rowData?.id))
            }),
            rowData => ({
              icon: () => (
                <FontAwesomeIcon
                  icon={faPen}
                  color={!rowData?.establishmentUuid ? "rgba(2, 42, 92, 0.5)" : "#022A5C"}
                  size="xs"
                />
              ),
              tooltip: !rowData?.establishmentUuid ? 'Categoria padrão não pode ser editada' : 'Editar categoria',
              disabled: !rowData?.establishmentUuid,
              onClick: () => {
                dispatch(setSingleProductCategory(rowData));
                setIsCategoryRegistrationModalOpen(true);
              }
            })
          ]}
        />
      )}
      <Grid
        container
        xs={12}
        style={{ padding: 10, display: 'flex', justifyContent: 'flex-end' }}
      >
        <Pagination
          color="primary"
          variant="outlined"
          shape="rounded"
          count={productsCategoriesPageCount}
          page={productsCategoriesPage}
          onChange={(_, page) => setProductsCategoriesPage(page)}
        />
      </Grid>
    </Paper>
  );
}